<template>
  <FlowBuilderPackage
    ref="flowBuilderRef"
    :flow="flow"
    :preview="preview"
    :can-add="canAdd"
    :template-preview="templatePreview"
    :zoom="zoom"
    :segment-groups="segmentGroups"
    :questions="questions"
    :widgets="widgets"
    :offers="offers"
    :phrases="phrases"
    :partners="partners"
    :pages="pages"
    :icons="icons"
    :get-external-url="getExternalUrl"
    :custom-field-label="customFieldLabel"
    :tag-label="tagLabel"
    :describe-logic="describeLogic"
    :adapter="adapter"
    :get-audience-breakdown="getAudienceBreakdown"
    :get-node-metrics="getNodeMetrics"
    :get-raw-node-metrics="getRawNodeMetrics.value"
    :get-node-sync-status="getNodeSyncStatus"
    :get-node-test="getNodeTest"
    :get-split-test-metrics="getSplitTestMetrics.value"
    :integration-data-version="integrationDataVersion"
    :get-variant-diff="getVariantDiff"
    :has-answer-changed="hasAnswerChanged"
    :get-variant-change-styles="getVariantChangeStyles"
    :get-offer-variant-diff="getOfferVariantDiff"
    :show-heat-map="showHeatMap"
    :show-path-flow="showPathFlow"
    :max-visitor-count="maxVisitorCount"
    :settings="globalSettings"
  >
    <!-- Pass through all slots -->
    <template #controls="slotProps">
      <FlowNodeAdder
        ref="nodeAdderRef"
        v-if="!preview && canAdd"
        :initial-tab="nodeAdderTab"
        @focus-node="($event) => slotProps.centerOnNode($event)"
      />
      <FlowZoomer
        v-if="!preview"
        :scale="slotProps.scale"
        :can-zoom-in="slotProps.canZoomIn"
        :can-zoom-out="slotProps.canZoomOut"
        :can-undo="props.canUndo"
        :can-redo="props.canRedo"
        @zoom-in="slotProps.zoom(1)"
        @zoom-out="slotProps.zoom(-1)"
        @center="slotProps.center()"
        @preview="handleShowPreview"
        @copy="copy()"

        @undo="emit('undo')"
        @redo="emit('redo')"
        @create-flow-test="emit('createFlowTest')"
      />
    </template>

    <template #minimap="slotProps">
      <FlowMap
        v-if="!preview && minimapVisible"
        :funnel="funnel"
        :small="templatePreview"
        :flow-builder-dimensions="slotProps.flowBuilderDimensions"
        :flow-builder-container-dimensions="
          slotProps.flowBuilderContainerDimensions
        "
        :content-dimensions="slotProps.contentDimensions"
        :transform="slotProps.transform"
      />
    </template>

    <template #heat-map-overlay="slotProps">
      <HeatMapOverlay
        v-if="useEnhancedReporting"
        :connections="slotProps.connections"
        :node-elements="slotProps.nodeElements"
      />
    </template>

    <template #preview-dialog>
      <Dialog
        v-model:visible="showPreview"
        :modal="true"
        :header="`Previewing ${flow?.name}`"
        class="w-[60rem]"
        @hide="handleDialogHide"
      >
        <template #header>
          <div>
            <div class="text-sm">
              Previewing
            </div>
            <div class="flex items-center space-x-4">
              <div class="font-bold text-lg">
                {{ flow?.name }}
              </div>
              <div>
                <Button
                  size="xs"
                  outlined
                  icon="pi pi-refresh"
                  label="Reload"
                  @click="handleReloadPreview"
                />
              </div>
            </div>
          </div>
        </template>
        <div>
          <iframe
            v-if="previewSource"
            ref="previewIframe"
            :src="previewSource"
            class="iframe-resize w-full"
            :style="{ height: iframeHeight + 'px' }"
          />
        </div>
      </Dialog>
    </template>
  </FlowBuilderPackage>

  <!-- Heat Map Legend -->
  <HeatMapLegend
    v-if="useEnhancedReporting"
    :max-visitor-count="maxVisitorCount"
  />

  <!-- Path Flow Analytics Panel -->
  <PathFlowPanel
    v-if="flow?.id && useEnhancedReporting"
    :flow-id="flow.id"
  />

  <!-- Node Search Dialog -->
  <FlowSearch
    v-model:open="searchOpen"
    @select-node="handleSearchSelectNode"
  />
</template>

<script setup lang="ts">
import { FlowBuilder as FlowBuilderPackage } from '@rightmessage/flow-builder'
import {
  ChatBubbleBottomCenterIcon as QuestionIcon,
  ArrowPathIcon as LoopIcon,
  SpeakerWaveIcon as OpenEndedIcon,
  GiftIcon,
  InboxIcon,
  CodeBracketIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  PresentationChartLineIcon
} from '@heroicons/vue/24/outline'
import { describeLogic as describeLogicUtil } from '@/modules/segment/services/logicObjectDescriber'
import { segmentGroupSyncSetupCompletion } from '@/modules/segment/services/segmentUtilities'
import iframePreview from '@/services/iframePreview'
import Dialog from 'primevue/dialog'
import FlowNodeAdder from './FlowNodeAdder.vue'
import FlowZoomer from './FlowZoomer.vue'
import FlowMap from './FlowMap.vue'
import FlowSearch from './FlowSearch.vue'
import HeatMapLegend from './HeatMapLegend.vue'
import PathFlowPanel from './PathFlowPanel.vue'
import HeatMapOverlay from './HeatMapOverlay.vue'
import { useRouter } from 'vue-router'
import { resolveComponent } from 'vue'
import { ROUTES } from '@/router/routeNames'
import { funnelDefinitionEach } from '@rightmessage/flow-builder'
import { makeId, idPrefixes } from '@/services/uuid'
import { useFlowBuilderAdapter } from '../composables/useFlowBuilderAdapter'
import {
  getVariantDiff,
  hasAnswerChanged,
  getVariantChangeStyles,
  getOfferVariantDiff
} from '@/modules/splittesting/services/testingUtilities'
import { useSplitTestMetricsStore } from '@/modules/splittesting/stores/splitTestMetricsStore'

const props = defineProps({
  flow: Object,
  preview: Boolean,
  canAdd: { type: Boolean, default: true },
  templatePreview: { type: Boolean, default: false },
  zoom: { type: Number, default: 1 },
  canUndo: { type: Boolean, default: false },
  canRedo: { type: Boolean, default: false },
  adapter: { type: Object, default: null }, // Accept adapter from parent
  splitTestVariantId: { type: String, default: null }, // Active variant ID for preview
  splitTestFlowId: { type: String, default: null } // Flow ID for split test preview
})

const emit = defineEmits(['undo', 'redo', 'createFlowTest'])

// Router
const router = useRouter()

// Stores
const widgetStore = useWidgetStore()
const offerStore = useOfferStore()
const phraseStore = usePhraseStore()
const mediaStore = useMediaStore()
const segmentGroupStore = useSegmentGroupStore()
const questionStore = useQuestionStore()
const integrationStore = useIntegrationStore()
const notificationStore = useNotificationStore()
const flowBuilderStore = useFlowBuilderStore()
const flowStore = useFlowStore()
const reportStore = useReportStore()
const splitTestStore = useSplitTestStore()
const splitTestMetricsStore = useSplitTestMetricsStore()
const flowMetricsStore = useFlowMetricsStore()
const settingsStore = useSettingsStore()
const pageStore = usePageStore()
const { settings: globalSettings } = storeToRefs(settingsStore)

// Check if enhanced flow reporting is enabled
const useEnhancedReporting = computed(
  () => globalSettings.value?.enhancedFlowReporting === true
)

// Preview handling
const showPreview = ref(false)
const iframeHeight = ref(600)
const previewIframe = ref<HTMLIFrameElement | null>(null)

// Node adder tab control
const nodeAdderTab = ref('add')

// Node adder ref for keyboard shortcut
const nodeAdderRef = ref<InstanceType<typeof FlowNodeAdder> | null>(null)

// Flow builder package ref for accessing exposed methods
const flowBuilderRef = ref<InstanceType<typeof FlowBuilderPackage> | null>(null)

// Search dialog state
const searchOpen = ref(false)

// Minimap visibility - persisted to localStorage
const minimapVisible = useStorage('flow-builder-minimap-visible', true)

// Toggle FlowNodeAdder panel: Cmd/Ctrl + ,
onKeyStroke(',', (e) => {
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault()
    nodeAdderRef.value?.toggle()
  }
})

// Toggle minimap: Cmd/Ctrl + M
onKeyStroke('m', (e) => {
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault()
    minimapVisible.value = !minimapVisible.value
  }
})

// Integration data version tracking for reactivity
const integrationDataVersion = ref(0)
const { data: customFieldsData } = useIntegrationData(
  'partner',
  'custom_fields'
)
const { data: tagsData } = useIntegrationData('partner', 'tags')
const { data: listsData } = useIntegrationData('partner', 'lists')

// Initialize flow metrics query if we have a flow and enhanced reporting is enabled
// Skip initialization if in preview mode - the parent component should handle it
if (props.flow?.id && globalSettings.value?.enhancedFlowReporting && !props.preview) {
  flowMetricsStore.initFlowMetricsQuery(props.flow.id)
}

// Watch for flow ID changes to initialize new queries
watch(
  () => props.flow?.id,
  (newId) => {
    // Skip initialization if in preview mode
    if (newId && useEnhancedReporting.value && !props.preview) {
      flowMetricsStore.initFlowMetricsQuery(newId)
    }
  }
)

// Watch for integration data changes and increment version
let previousDataHash = ''
watchEffect(() => {
  // Create a hash of the current data to detect actual changes
  const currentDataHash = JSON.stringify({
    customFields: customFieldsData.value?.length || 0,
    tags: tagsData.value?.length || 0,
    lists: listsData.value?.length || 0
  })

  // Only increment version if data actually changed
  if (previousDataHash && previousDataHash !== currentDataHash) {
    integrationDataVersion.value++
  }

  previousDataHash = currentDataHash
})

const funnel = computed(() => props.flow.funnelDefinition)

const segmentGroups = computed(() => segmentGroupStore.segmentGroups)
const questions = computed(() =>
  questionStore.questions.map((q) => ({
    ...q,
    segmentGroupId: q.dimensionId // Map dimensionId to segmentGroupId for package compatibility
  }))
)
const widgets = computed(() => widgetStore.widgets)
const offers = computed(() => offerStore.offers)
const phrases = computed(() => phraseStore.phrases)
const partners = computed(() =>
  integrationStore.integratedPartner ? [integrationStore.integratedPartner] : []
)
const pages = computed(() => pageStore.pages)

// Icon collection
const icons = {
  QuestionIcon,
  LoopIcon,
  OpenEndedIcon,
  GiftIcon,
  InboxIcon,
  CodeBracketIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  PresentationChartLineIcon
}

// Helper functions
const getExternalUrl = (url: string) => mediaStore.getExternalUrl(url)

const customFieldLabel = async (id: string) => {
  try {
    // Use the same pattern as the logic describer - fetch from integration data
    const data = await getIntegrationData('partner', 'custom_fields')
    const field = data.value.find((f: any) => String(f.id) === String(id))
    return field ? field.label : id
  } catch (error) {
    console.error('Error fetching custom field label:', error)
    return id
  }
}

const tagLabel = async (id: string) => {
  try {
    // Fetch tags data from integration
    const tagsData = await getIntegrationData('partner', 'tags')
    const tag = tagsData.value.find((t: any) => String(t.id) === String(id))
    if (tag) {
      return tag.name || tag.label || id
    }

    // If not found in tags, try lists
    const listsData = await getIntegrationData('partner', 'lists')
    const list = listsData.value.find((l: any) => String(l.id) === String(id))
    return list ? (list.name || list.label || id) : id
  } catch (error) {
    console.error('Error fetching tag label:', error)
    return id
  }
}

const describeLogic = async (logic: any, context: any = {}) => {
  // Prepare options for the webapp's describeLogic function
  let customFields = []
  let tags = []
  let lists = []

  try {
    // Fetch integration data if partner is connected
    if (integrationStore.integratedPartner?.name) {
      const [fieldsData, tagsData, listsData] = await Promise.all([
        getIntegrationData('partner', 'custom_fields'),
        getIntegrationData('partner', 'tags'),
        getIntegrationData('partner', 'lists')
      ])

      customFields = fieldsData.value || []
      tags = tagsData.value || []
      lists = listsData.value || []
    }
  } catch (error) {
    console.error(
      'Error fetching integration data for logic description:',
      error
    )
  }

  const options = {
    preview: context.preview || false,
    customFields,
    tags,
    lists
  }

  return await describeLogicUtil(logic, options)
}

// Get audience breakdown for segment groups
const getAudienceBreakdown = (segmentGroupId: string) => {
  if (!segmentGroupId) return null
  const breakdown = segmentGroupStore.getAudienceBreakdownForId(segmentGroupId)
  return breakdown
}

// Get metrics for nodes
const getNodeMetrics = (node: any) => {
  if (!node || props.preview || props.templatePreview) return null

  // Use enhanced reporting if enabled
  if (useEnhancedReporting.value) {
    // Check flow execution metrics for ALL node types
    const nodeId = node.id || node._uuid
    if (nodeId && props.flow?.id) {
      // First check if we have batch data (when in preview mode)
      const batchMetrics = flowMetricsStore.getMetricsFromBatch(props.flow.id)
      if (batchMetrics?.nodeMetrics?.[nodeId]) {
        const nodeMetrics = batchMetrics.nodeMetrics[nodeId]
        return flowMetricsStore.formatNodeMetrics(nodeMetrics)
      }
      
      // Otherwise use individual query data
      const nodeMetrics = flowMetricsStore.getNodeMetrics(
        props.flow.id,
        nodeId,
        node.type
      )
      if (nodeMetrics) {
        return flowMetricsStore.formatNodeMetrics(nodeMetrics)
      }
    }
    // Return empty array if no metrics found in enhanced reporting
    return []
  }

  // Legacy report store for when enhanced reporting is disabled
  // For SG nodes that are questions (ask=true)
  if (node.type === 'sg' && node.ask && node.segmentGroupId) {
    const question = questionStore.getBySegmentGroupId(node.segmentGroupId)
    if (question) {
      const metricsFunc = reportStore.metricsForQuestion
      const metrics = metricsFunc(question)
      return metrics
    }
  }

  // Legacy report store for offers
  if (node.type === 'offer' && node.offerId) {
    const offer = offerStore.getById(node.offerId)
    if (offer) {
      const metricsFunc = reportStore.metricsForOffer
      const metrics = metricsFunc(offer)
      return metrics
    }
  }

  // For other node types in legacy mode, return empty metrics
  return []
}

// Create a reactive version of getRawNodeMetrics
const getRawNodeMetrics = computed(() => {
  // Return a function that accesses reactive data
  return (node: any) => {
    if (
      !node ||
      props.preview ||
      props.templatePreview ||
      !useEnhancedReporting.value
    )
      return null

    // Use the same logic as getNodeMetrics for consistency
    // For yesno nodes specifically, we need to check the node ID
    const nodeId = node.id || node._uuid

    if (nodeId && props.flow?.id) {
      const flowId = props.flow.id

      // Access the flowQueries to ensure reactivity
      const query = flowMetricsStore.flowQueries[flowId]

      // First check if query has data
      if (query?.data?.value) {
        const queryMetrics = query.data.value.nodeMetrics?.[nodeId]
        if (queryMetrics) return queryMetrics
      }

      // Fall back to cache
      const cachedMetrics = flowMetricsStore.nodeMetricsCache[flowId]?.[nodeId]
      return cachedMetrics || null
    }

    return null
  }
})

// Get sync status for segment group nodes
const getNodeSyncStatus = (node: any) => {
  // Check for question nodes (type 'sg' with ask=true)
  if (node.type === 'sg' && node.ask && node.segmentGroupId) {
    const segmentGroup = segmentGroupStore.getById(node.segmentGroupId)
    if (segmentGroup) {
      const syncStatus = segmentGroupSyncSetupCompletion(segmentGroup)
      return syncStatus
    }
  }
  return null
}

// Get active test for a node - make this reactive by accessing splitTests
const getNodeTest = (node: any) => {
  // Access splitTests to ensure reactivity
  const allTests = splitTestStore.splitTests

  // Handle question nodes
  if (node.segmentGroupId) {
    // First, find the question ID from the segment group ID
    const question = questionStore.questions.find(
      (q) => q.dimensionId === node.segmentGroupId
    )
    if (!question) return null

    // Now look for tests using the question ID
    const tests = splitTestStore.getForResource('question', question.id)

    // Return the first active test
    return tests.find(
      (test) => test.status === 'active' || test.status === 'draft'
    )
  }

  // Handle offer nodes
  if (node.type === 'offer' && node.offerId) {
    const tests = splitTestStore.getForResource('offer', node.offerId)

    // Return the first active test
    return tests.find(
      (test) => test.status === 'active' || test.status === 'draft'
    )
  }

  return null
}

// Get split test metrics - computed that returns a function, just like metricsForQuestion
const getSplitTestMetrics = computed(() => {
  return (testPid: string) => {
    // Access the entire cache first to ensure Vue tracks the dependency
    const cacheValue = splitTestMetricsStore.metricsCache || {}
    const hasKey = testPid in cacheValue
    const metrics = hasKey ? cacheValue[testPid] : null
    return metrics
  }
})

// Fetch metrics for active split tests when component mounts
const fetchActiveSplitTestMetrics = async () => {
  try {
    // Get all active split tests for questions and offers
    const activeSplitTests = splitTestStore.splitTests.filter(
      (test) =>
        test.status === 'active' &&
        (test.resource_type === 'question' || test.resource_type === 'offer')
    )

    // Fetch metrics for each active test using the store
    for (const test of activeSplitTests) {
      if (test.pid) {
        await splitTestMetricsStore.fetchMetrics(test.pid)
      }
    }
  } catch (error) {
    console.error('Failed to fetch split test metrics:', error)
  }
}

// Fetch flow execution metrics for all nodes
const fetchFlowExecutionMetrics = async () => {
  try {
    if (
      props.flow?.id &&
      !props.preview &&
      !props.templatePreview &&
      useEnhancedReporting.value
    ) {
      // Use the fetchMetricsForFlows method instead
      await flowMetricsStore.fetchMetricsForFlows([props.flow.id])
    }
  } catch (error) {
    console.error('Failed to fetch flow execution metrics:', error)
  }
}

// Watch for changes to split tests and refetch metrics
watch(
  () => splitTestStore.splitTests,
  (newTests) => {
    // Check if any tests have changed to active status
    const hasNewActiveTests = newTests.some(
      (test) =>
        test.status === 'active' && !splitTestMetricsStore.getMetrics(test.pid)
    )

    if (hasNewActiveTests) {
      fetchActiveSplitTestMetrics()
    }
  },
  { deep: true }
)

// Watch for date range changes to refetch flow metrics
watch(
  () => reportStore.dateRange,
  () => {
    if (
      props.flow?.id &&
      !props.preview &&
      !props.templatePreview &&
      useEnhancedReporting.value
    ) {
      fetchFlowExecutionMetrics()
    }
  },
  { deep: true }
)

// Store interval ID for cleanup
let metricsInterval: number | undefined

// Use the adapter from the composable as base
const baseAdapter = useFlowBuilderAdapter(props)

// Merge with any adapter passed from parent (e.g., onDropzoneHintSelect from FlowShow)
// Parent adapter properties take precedence for custom handlers
const adapter = {
  ...baseAdapter,
  ...(props.adapter || {}),

  // Add search handler (wrapper-specific)
  onOpenSearch: () => {
    searchOpen.value = true
  },

  // Add undo/redo handlers that emit to parent
  onUndo: () => {
    // Call parent's onUndo if provided, otherwise emit
    if (props.adapter?.onUndo) {
      props.adapter.onUndo()
    } else {
      emit('undo')
    }
  },

  onRedo: () => {
    // Call parent's onRedo if provided, otherwise emit
    if (props.adapter?.onRedo) {
      props.adapter.onRedo()
    } else {
      emit('redo')
    }
  }
}

// Handle node selection from search
const handleSearchSelectNode = (uuid: string) => {
  const node = flowBuilderRef.value?.findNodeByUuid(uuid)
  if (node) {
    // Center the viewport on the selected node
    flowBuilderRef.value?.centerOnNode(node)

    // Open the drawer for the node (unless it's an exit node which has no configuration)
    if (node.type !== 'exit') {
      const currentRoute = router.currentRoute.value
      router.push({
        name: ROUTES.OFFERFUNNELNODE,
        params: {
          offerFunnelId: currentRoute.params.offerFunnelId,
          uuid: uuid
        }
      })
    }
  }
}

// Provide the adapter directly to child components
provide('adapter', adapter)

// Heat map and path flow state
const { showHeatMap, showPathFlow } = storeToRefs(flowBuilderStore)

// Calculate maximum visitor count across all nodes
const maxVisitorCount = computed(() => {
  if (
    !props.flow?.id ||
    props.preview ||
    props.templatePreview ||
    !useEnhancedReporting.value
  )
    return 0

  const query = flowMetricsStore.flowQueries[props.flow.id]
  if (!query?.data?.value?.nodeMetrics) return 0

  let max = 0
  for (const metrics of Object.values(query.data.value.nodeMetrics) as any[]) {
    if (metrics.visitorCount && metrics.visitorCount > max) {
      max = metrics.visitorCount
    }
  }

  return max
})

// Copy functionality
const copy = () => {
  const json = createTemplateFromFlow(props.flow)
  copyToClipboard(JSON.stringify(json, null, 2))
}

// Preview handling
const associatedWidgets = computed(() =>
  widgetStore.getWidgetsForOfferFunnel(props.flow.id)
)

const previewSource = computed(() => {
  return iframePreview({
    widget: associatedWidgets.value[0] || false,
    flow: props.flow,
    splitTestVariantId: props.splitTestVariantId || undefined,
    splitTestFlowId: props.splitTestFlowId || undefined,
  })
})

const handleIframeMessage = (event) => {
  if (event.origin !== new URL(previewSource.value).origin) return

  if (event.data.type === 'resize') {
    iframeHeight.value = event.data.height
  }
}

onMounted(() => {
  // Add iframe message listener
  window.addEventListener('message', handleIframeMessage)

  // Fetch split test metrics
  fetchActiveSplitTestMetrics()

  // Fetch flow execution metrics
  fetchFlowExecutionMetrics()

  // Refresh metrics periodically
  metricsInterval = setInterval(() => {
    fetchActiveSplitTestMetrics()
    fetchFlowExecutionMetrics()
  }, 30000) // Every 30 seconds
})

onUnmounted(() => {
  // Remove iframe message listener
  window.removeEventListener('message', handleIframeMessage)

  // Clear metrics interval
  if (metricsInterval) {
    clearInterval(metricsInterval)
  }
})

// Handle showing preview
const handleShowPreview = () => {
  showPreview.value = true
}

// Handle dialog hide event
const handleDialogHide = () => {
  showPreview.value = false
}

// Handle preview reload
const handleReloadPreview = () => {
  if (previewIframe.value) {
    previewIframe.value.src = previewIframe.value.src
  }
}

// Expose methods from the package's FlowBuilder for parent components
defineExpose({
  findNodeByUuid: (uuid: string) => flowBuilderRef.value?.findNodeByUuid(uuid),
  centerOnNode: (node: any) => flowBuilderRef.value?.centerOnNode(node),
  center: () => flowBuilderRef.value?.center(),
  zoom: (amount: number) => flowBuilderRef.value?.zoom(amount)
})
</script>
